// The whole state, enumerated once, with the type each key holds.
//
// `store.get('practice.status')` is a string the compiler cannot check: a typo reads undefined and
// nothing complains until a user notices. Going through this registry — `store.get(K.practice.status)`
// — gives the key autocompletion and the read a type, and makes this file the one place that says
// what the application's state is.
//
// Legacy modules still address the store with string literals; each one drops them as it is rewritten.

import type { CurrentUser } from "./slices/user.ts";
import type { CatalogLine } from "../services/catalog.ts";
import type { OwnLine } from "../domain/myLines.ts";
import type { StudyListItem } from "../domain/studyList.ts";
import type { Progress } from "../domain/progressRules.ts";
import type { Verdict } from "../domain/moveRating.ts";
import type { EndReason } from "../domain/practiceRules.ts";
import type { ResumablePractice } from "../domain/practiceSnapshot.ts";
import type { Outcome } from "../domain/playRules.ts";
import type { HandoverReason, PartnerSource, PartnerUsing } from "../domain/sparringPartner.ts";
import type { MemoryGameKey, MemorySource } from "../domain/memoryGames.ts";
import type { Journey } from "../domain/pieceJourney.ts";
import type { PieceRole } from "../domain/placement.ts";
import type {
    DrillPosition, DrillResult, ExerciseCount, GamePhase, PositionExercise, RatedMove, ResultRow, SetUp,
} from "../domain/positions.ts";
import type { RepertoireReview, RepertoireReviewRow } from "../services/api.ts";
import { storeKey as key } from "./createStore.ts";

export type { StoreKey } from "./createStore.ts";

export type PracticeStatus = "idle" | "ready" | "playing" | "offbook" | "finished" | "locked" | "unavailable";
export type EngineLoadState = "idle" | "loading" | "ready" | "error";
export type Side = "white" | "black";
/** Who may move pieces on a board: a side, nobody, or whoever is to move. */
export type MovableSide = Side | "none" | null;
/** A game against the sparring partner: nothing started, being played, or over. */
export type PlayStatus = "idle" | "playing" | "finished";
/** A blindfold game: nothing started, being played, or over. */
export type BlindfoldStatus = "idle" | "playing" | "finished";
/** What is being done without seeing the board: a whole game, or one piece walked across it. */
export type BlindfoldExercise = "game" | "journey" | "squares";
/** A journey: choosing, walking it, told how it went, and the run over. */
export type JourneyStatus = "idle" | "playing" | "checked" | "finished";
/** A round of "light or dark": choosing, answering against the clock, and over. */
export type SquaresStatus = "idle" | "playing" | "finished";
/** A round of a memory game: choosing, looking at it, answering, told how it went, and the run over. */
export type RepertoiresStatus = "loading" | "signedOut" | "list" | "question" | "checking" | "answered" | "roundOver";

/** What one answer of a review was: the move played, what the repertoire plays there, and the streak it left. */
export interface RepertoireAnswer {
    right: boolean;
    playedSan: string;
    expectedSans: string[];
    streak: number;
}

export type PositionsStatus = "loading" | "signedOut" | "picker" | "question" | "thinking" | "retry" | "playing"
    | "answered" | "roundOver";

/** A try at a "find" or an "opening" that was not the best move: what it did, never what the best is. */
export interface PositionTry {
    verdict: "best" | "keeps" | "slips" | "loses";
    text: string;
    tries: number;
    triesLeft: number;
    thinkMs: number;
    thinkNote: string | null;
}

/** What an answer to one of your positions was worth, and what the game had. */
export interface PositionAnswer {
    result: DrillResult;
    /** The move given and the best one, in SAN; for "convert", the score it ended at and the one it had to keep. */
    yourSan: string | null;
    bestSan: string | null;
    loss: number | null;
    endCp: number | null;
    targetCp: number | null;
    /** What happened in the game, said once the answer is in. */
    gameLine: string;
    xp: number;
    /** An opening put right, in UCI, for the study list. */
    line: string[] | null;
    /** The moves set against the position for the engine card: yours, the best, the game's, scored for the user. */
    notes: { label: string; san: string | null; cp: number | null }[];
    /** How many tries it took, how long the last one was thought about, and what that is worth saying. */
    tries: number;
    thinkMs: number | null;
    thinkNote: string | null;
    /** The result as a table: every try (or every move of a "convert"), the game's move and the best. */
    rows: ResultRow[];
    /** The moves the user played, rated: a row of a "convert" puts its position back on the board. */
    moves: RatedMove[];
    /** "convert": a move was taken back, which made it a miss. */
    tookBack: boolean;
}

export interface PositionsConvert {
    pliesLeft: number;
    partner: string;
    /** The user's clock, counting down, when the round is played with the game's clock. */
    clockMs: number | null;
    /** A move of the user's can be taken back now (it is their turn and they have played one), and one was. */
    canTakeBack: boolean;
    tookBack: boolean;
}

export type MemoryStatus = "idle" | "showing" | "answering" | "checked" | "finished";

/** A piece taken off the board, waiting in the tray to go back. */
export interface MemoryTrayPiece {
    color: Side;
    role: PieceRole;
    /** One of its kind is already back on the board, so this one has been used. */
    placed: boolean;
}

/** How a round went: the same shape for both games, so one card paints either. */
export interface MemoryRoundCheck {
    score: number;
    correct: number;
    total: number;
    /** What the round was made of: a piece and its square, or a move to repeat. */
    items: { label: string; right: boolean }[];
}

/** Repeating the moves: how far along, and how many were not played right first time. */
export interface MemorySequence {
    total: number;
    index: number;
    missed: number;
}

/** Looking at a finished game: every move of yours gets the verdict the study would give it. */
/**
 * Who answered the sparring partner's moves. `using` is what answered the last one, so the card
 * beside the board can say it while the game is on; `handover` is set once, the move the book ran
 * out on, because a player who asked for real moves has to be told when they stop.
 */
export interface PartnerState {
    source: PartnerSource;
    using: PartnerUsing;
    /** Partner moves that came out of real games, and out of the engine. */
    book: number;
    engine: number;
    handover: { moveNumber: number; why: HandoverReason } | null;
}

export interface PlayReview {
    status: "idle" | "running" | "done" | "failed";
    done: number;
    total: number;
    /** By ply: what the move was worth. */
    verdicts: Record<number, { code: Verdict; loss: number | null; bestSan: string | null }>;
    accuracy: number | null;
}

/** One move of the practice, indexed by its ply; the line's own moves stay undefined. */
export interface PracticeMove {
    ply: number;
    actor: "user" | "rival";
    san: string;
    uci: string;
    fenBefore: string;
    label: string;                       // "6...Bg7"
    verdict: {
        status: "pending" | "rated" | "unavailable";
        code?: Verdict;
        loss?: number | null;
        accuracy?: number | null;
        bestSan?: string;
        bestUci?: string;
        /** The best move's score and the one played's, in centipawns from the mover's side (a mate as `scoreToCp`). */
        bestCp?: number | null;
        playedCp?: number | null;
        source?: string;
    };
    insight: { code: string; text: string; tone: string } | null;
    /** How often the sparring partner's reply is played, when the book knows. */
    sharePct?: number;
    stats?: { db?: unknown; mine?: unknown; repertoire?: unknown };
    xp: number;
    combo: number;
    hinted: boolean;
}

export interface PracticeResult {
    title: string;
    counted: boolean;
    isRetry: boolean;
    ownMoves: number;
    accuracy: number | null;
    endReason: EndReason;
    [extra: string]: unknown;
}

/** The line being practised: a catalog line, or one the user created. */
export type PracticeLine = CatalogLine & { source: "catalog" | "user"; id?: number };

/**
 * The keys of one board, by the name it was made with. A function rather than a constant because
 * how many boards a page has is up to the page: the study has one, /lines has another, and the
 * tools on the way will have more.
 */
export const boardKeys = (id: string) => ({
    fen: key<string>(`boards.${id}.fen`),
    turn: key<Side>(`boards.${id}.turn`),
    history: key<string[]>(`boards.${id}.history`),
    gameOver: key<boolean>(`boards.${id}.gameOver`),
    lastMove: key<[string, string] | null>(`boards.${id}.lastMove`),
    orientation: key<Side>(`boards.${id}.orientation`),
    movableSide: key<MovableSide>(`boards.${id}.movableSide`),
    baseFen: key<string | null>(`boards.${id}.baseFen`),
    baseTurn: key<"w" | "b">(`boards.${id}.baseTurn`),
    baseFullmove: key<number>(`boards.${id}.baseFullmove`),
});

export const K = {
    user: {
        currentUser: key<CurrentUser | null>("user.currentUser"),
        lichessConnected: key<boolean>("user.lichessConnected"),
        lichessUsername: key<string | null>("user.lichessUsername"),
    },
    lines: {
        /** The account's own lines, as the server last answered. Null until they have been read. */
        own: key<OwnLine[] | null>("lines.own"),
        /** The window the ranking of their games is read over, in days. Null until read from storage. */
        window: key<number | null>("lines.window"),
    },
    studyList: {
        /** What the user said they want to work on. Null until a page has read it. */
        items: key<StudyListItem[] | null>("studyList.items"),
    },
    practice: {
        status: key<PracticeStatus>("practice.status"),
        line: key<PracticeLine | null>("practice.line"),
        hasTree: key<boolean>("practice.hasTree"),
        moves: key<(PracticeMove | undefined)[]>("practice.moves"),
        revision: key<number>("practice.revision"),
        ownMoves: key<number>("practice.ownMoves"),
        combo: key<number>("practice.combo"),
        bestCombo: key<number>("practice.bestCombo"),
        xp: key<number>("practice.xp"),
        accuracy: key<number | null>("practice.accuracy"),
        offbook: key<{ rarity: "rare" | "unknown" | "paused" | "repertoire" } | null>("practice.offbook"),
        notice: key<string | null>("practice.notice"),
        hintUci: key<string | null>("practice.hintUci"),
        isRetry: key<boolean>("practice.isRetry"),
        resumable: key<ResumablePractice | null>("practice.resumable"),
        result: key<PracticeResult | null>("practice.result"),
        opening: key<string | null>("practice.opening"),
    },
    progress: {
        loaded: key<boolean>("progress.loaded"),
        source: key<"local" | "server">("progress.source"),
        summary: key<Progress | null>("progress.summary"),
        imported: key<boolean>("progress.imported"),
        anonymousLimit: key<number>("progress.anonymousLimit"),
    },
    engine: {
        activeBuild: key<string | null>("engine.activeBuild"),
        identity: key<string | null>("engine.identity"),
        loadState: key<EngineLoadState>("engine.loadState"),
        latestScorePawns: key<number | null>("engine.latestScorePawns"),
        latestScoreText: key<string | null>("engine.latestScoreText"),
        evalGeneration: key<number>("engine.evalGeneration"),
        isEvaluating: key<boolean>("engine.isEvaluating"),
        latestDepth: key<number | null>("engine.latestDepth"),
        isEnabled: key<boolean>("engine.isEnabled"),
        error: key<string | null>("engine.error"),
        showLines: key<boolean>("engine.showLines"),
        /** The number and the bar hidden, for whoever plays without them. */
        evalHidden: key<boolean>("engine.evalHidden"),
        pvLines: key<Record<number, unknown>>("engine.pvLines"),
    },
    board: {
        /** The side the user plays, which is not a property of a board: My Games filters by it too. */
        userSide: key<Side>("board.userSide"),
    },
    /** `K.boards("study").fen`: the keys of one board, by the name it was made with. */
    boards: boardKeys,
    book: {
        latestMoves: key<unknown[] | null>("book.latestMoves"),
        isLoading: key<boolean>("book.isLoading"),
        error: key<string | null>("book.error"),
        stats: key<unknown>("book.stats"),
        fen: key<string | null>("book.fen"),
        source: key<string | null>("book.source"),
        opening: key<unknown>("book.opening"),
    },
    myGames: {
        latestMoves: key<unknown[] | null>("myGames.latestMoves"),
        plans: key<unknown>("myGames.plans"),
        moveDiffs: key<unknown>("myGames.moveDiffs"),
        fen: key<string | null>("myGames.fen"),
        isLoading: key<boolean>("myGames.isLoading"),
        error: key<string | null>("myGames.error"),
        stats: key<unknown>("myGames.stats"),
    },
    repertoire: {
        latestMoves: key<unknown[] | null>("repertoire.latestMoves"),
        isLoading: key<boolean>("repertoire.isLoading"),
        error: key<string | null>("repertoire.error"),
        status: key<unknown>("repertoire.status"),
        fen: key<string | null>("repertoire.fen"),
        hintMode: key<string | null>("repertoire.hintMode"),
    },
    config: {
        applyingStoredConfig: key<boolean>("config.applyingStoredConfig"),
        debugOn: key<boolean>("config.debugOn"),
        sparringSummary: key<unknown>("config.sparringSummary"),
    },
    /** The game against the sparring partner, on /tools/play. */
    play: {
        status: key<PlayStatus>("play.status"),
        side: key<Side>("play.side"),
        strength: key<string>("play.strength"),
        /** Real moves or the engine: which partner was asked for. */
        source: key<PartnerSource>("play.source"),
        /** The catalog opening the game starts in, by slug. Null is the first move of the game. */
        opening: key<string | null>("play.opening"),
        /** The openings there are to start from. Null until the catalog has been read. */
        openings: key<CatalogLine[] | null>("play.openings"),
        /** Who has been answering, and what happened when the book ran out. */
        partner: key<PartnerState>("play.partner"),
        /** How many plies were dealt before the game began: the opening is not your play. */
        fromPly: key<number>("play.fromPly"),
        thinking: key<boolean>("play.thinking"),
        /** The game is stopped and the position is being looked at. Nothing moves while it is. */
        paused: key<boolean>("play.paused"),
        outcome: key<Outcome | null>("play.outcome"),
        xp: key<number>("play.xp"),
        saved: key<boolean>("play.saved"),
        /** The finished game, for whoever wants to take it away: its PGN and its last position. */
        pgn: key<string | null>("play.pgn"),
        fen: key<string | null>("play.fen"),
        error: key<string | null>("play.error"),
        review: key<PlayReview>("play.review"),
    },
    /** Your positions, on /tools/positions. */
    positions: {
        status: key<PositionsStatus>("positions.status"),
        summary: key<ExerciseCount[] | null>("positions.summary"),
        exercise: key<PositionExercise>("positions.exercise"),
        phase: key<GamePhase | null>("positions.phase"),
        withClock: key<boolean>("positions.withClock"),
        round: key<DrillPosition[]>("positions.round"),
        index: key<number>("positions.index"),
        setUp: key<SetUp | null>("positions.setUp"),
        answer: key<PositionAnswer | null>("positions.answer"),
        results: key<{ gameId: number; ply: number; result: DrillResult }[]>("positions.results"),
        convert: key<PositionsConvert | null>("positions.convert"),
        /** The board has been moved away from the moment, to look at it: "back" puts it where it was. */
        exploring: key<boolean>("positions.exploring"),
        /** The last try that was not the best, while another is allowed. */
        attempt: key<PositionTry | null>("positions.attempt"),
        /** When the question was put, for the clock of thinking the panel shows. */
        askedAt: key<number | null>("positions.askedAt"),
        xp: key<number>("positions.xp"),
        error: key<string | null>("positions.error"),
    },
    /** Your repertoires, on /tools/repertoires. */
    repertoires: {
        status: key<RepertoiresStatus>("repertoires.status"),
        tab: key<"forget" | "manage">("repertoires.tab"),
        review: key<RepertoireReview | null>("repertoires.review"),
        round: key<RepertoireReviewRow[]>("repertoires.round"),
        index: key<number>("repertoires.index"),
        answer: key<RepertoireAnswer | null>("repertoires.answer"),
        results: key<boolean[]>("repertoires.results"),
        error: key<string | null>("repertoires.error"),
    },
    /** The memory games, on /tools/memory. */
    memory: {
        status: key<MemoryStatus>("memory.status"),
        game: key<MemoryGameKey>("memory.game"),
        /** Where the position comes from: a real opening, or nowhere at all. */
        source: key<MemorySource>("memory.source"),
        level: key<string>("memory.level"),
        round: key<number>("memory.round"),
        scores: key<number[]>("memory.scores"),
        countdown: key<number>("memory.countdown"),
        lineName: key<string | null>("memory.lineName"),
        tray: key<MemoryTrayPiece[]>("memory.tray"),
        check: key<MemoryRoundCheck | null>("memory.check"),
        sequence: key<MemorySequence | null>("memory.sequence"),
        score: key<number>("memory.score"),
        xp: key<number>("memory.xp"),
        saved: key<boolean>("memory.saved"),
        error: key<string | null>("memory.error"),
    },
    /** Playing without seeing the board, on /tools/blindfold. */
    blindfold: {
        status: key<BlindfoldStatus>("blindfold.status"),
        side: key<Side>("blindfold.side"),
        strength: key<string>("blindfold.strength"),
        help: key<string>("blindfold.help"),
        thinking: key<boolean>("blindfold.thinking"),
        peeking: key<boolean>("blindfold.peeking"),
        peeks: key<number>("blindfold.peeks"),
        error: key<string | null>("blindfold.error"),
        outcome: key<Outcome | null>("blindfold.outcome"),
        xp: key<number>("blindfold.xp"),
        saved: key<boolean>("blindfold.saved"),
        /** The finished game, for whoever wants to take it away: its PGN and its last position. */
        pgn: key<string | null>("blindfold.pgn"),
        fen: key<string | null>("blindfold.fen"),
        /** The board is up again mid-game: the exercise stopped, the game did not. */
        revealed: key<boolean>("blindfold.revealed"),
        /** The moves of the game, kept whole while it is being walked through afterwards. */
        moves: key<string[]>("blindfold.moves"),
        /** Which move is being looked at once it is over. Null is the end of the game. */
        reviewPly: key<number | null>("blindfold.reviewPly"),
        /** Moves are pressed rather than typed, which is what a phone wants. */
        keypad: key<boolean>("blindfold.keypad"),
        /** Which exercise the tool is on: the game, or walking a piece across the board. */
        exercise: key<BlindfoldExercise>("blindfold.exercise"),
    },
    /** "Move the piece", the other exercise of /tools/blindfold. */
    journey: {
        status: key<JourneyStatus>("journey.status"),
        level: key<string>("journey.level"),
        round: key<number>("journey.round"),
        scores: key<number[]>("journey.scores"),
        /** The journey being walked: the piece, where it starts, where it is going, and par. */
        journey: key<Journey | null>("journey.journey"),
        /** The squares called so far, in order. Where the piece stands is the last of them. */
        path: key<string[]>("journey.path"),
        /** Squares called that the piece could not reach. They cost, so they are counted. */
        wrong: key<number>("journey.wrong"),
        /** The shortest way, once the round is over. Null while it is still being looked for. */
        shown: key<string[] | null>("journey.shown"),
        /** The way was asked for rather than found: the round is over and it is worth nothing. */
        gaveUp: key<boolean>("journey.gaveUp"),
        error: key<string | null>("journey.error"),
        score: key<number>("journey.score"),
        xp: key<number>("journey.xp"),
        saved: key<boolean>("journey.saved"),
    },
    /** "Light or dark", the third exercise of /tools/blindfold: a minute of squares and their colour. */
    squares: {
        status: key<SquaresStatus>("squares.status"),
        /** The square being asked about. Null before a round starts. */
        square: key<string | null>("squares.square"),
        /** Seconds left of the round. */
        left: key<number>("squares.left"),
        /** How many have been answered, and how many of those were right. */
        answered: key<number>("squares.answered"),
        right: key<number>("squares.right"),
        /** The run of right answers going on now, and the longest of the round. */
        streak: key<number>("squares.streak"),
        best: key<number>("squares.best"),
        /** The last answer, so the panel can flash it: the square, and whether it was right. */
        last: key<{ square: string; right: boolean } | null>("squares.last"),
        score: key<number>("squares.score"),
        title: key<string>("squares.title"),
        note: key<string>("squares.note"),
        /** The squares that caught you out, once the round is over. */
        missed: key<string[]>("squares.missed"),
        xp: key<number>("squares.xp"),
        saved: key<boolean>("squares.saved"),
    },
    studyLine: {
        name: key<string | null>("studyLine.name"),
        baseFen: key<string | null>("studyLine.baseFen"),
        uciMoves: key<string | null>("studyLine.uciMoves"),
        /** The line on the board is the opening of today's training: its plays, and the way back to the day. */
        routine: key<{ back: string; played: number; needed: number; canContinue: boolean } | null>("studyLine.routine"),
    },
} as const;

